import {
  BarChart3,
  BookOpen,
  Home,
  Library,
  ScanSearch
} from "lucide-react";
import { useMemo, useState } from "react";
import { Brand } from "./components/Brand";
import { birds, type Bird, type BirdGroup } from "./data/birds";
import { useProgress } from "./hooks/useProgress";
import { shuffle } from "./utils/random";
import { BirdDetailView } from "./views/BirdDetailView";
import { HomeView } from "./views/HomeView";
import { LearnView } from "./views/LearnView";
import { LibraryView } from "./views/LibraryView";
import { ProgressView } from "./views/ProgressView";
import { QuizView } from "./views/QuizView";

type View = "home" | "learn" | "quiz" | "library" | "detail" | "progress";
type LearningMode = "daily" | "review";

const uniqueBirds = (items: Bird[]) =>
  items.filter(
    (bird, index, current) =>
      current.findIndex((candidate) => candidate.id === bird.id) === index
  );

const App = () => {
  const {
    progress,
    stats,
    setMastery,
    markStudied,
    recordQuizAnswer,
    resetProgress
  } = useProgress();
  const [view, setView] = useState<View>("home");
  const [libraryGroup, setLibraryGroup] = useState<BirdGroup>();
  const [selectedBird, setSelectedBird] = useState<Bird>();
  const [learningCards, setLearningCards] = useState<Bird[]>([]);
  const [learningMode, setLearningMode] = useState<LearningMode>("daily");
  const [quizQuestions, setQuizQuestions] = useState<Bird[]>([]);

  const navigate = (nextView: View) => {
    setView(nextView);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startDailyLearning = () => {
    const mistakes = progress.mistakes
      .map((id) => birds.find((bird) => bird.id === id))
      .filter((bird): bird is Bird => Boolean(bird));
    const fuzzy = shuffle(
      birds.filter((bird) => progress.mastery[bird.id] === "fuzzy")
    );
    const learning = shuffle(
      birds.filter((bird) => progress.mastery[bird.id] === "new")
    );
    const unseen = shuffle(
      birds.filter((bird) => !progress.mastery[bird.id])
    );
    const known = shuffle(
      birds.filter((bird) => progress.mastery[bird.id] === "known")
    );
    const queue = uniqueBirds([
      ...mistakes,
      ...fuzzy,
      ...learning,
      ...unseen,
      ...known
    ]).slice(0, progress.dailyGoal);
    setLearningMode("daily");
    setLearningCards(queue);
    navigate("learn");
  };

  const startReview = () => {
    const reviewIds = new Set([
      ...progress.mistakes,
      ...birds
        .filter((bird) =>
          ["fuzzy", "new"].includes(progress.mastery[bird.id] ?? "")
        )
        .map((bird) => bird.id)
    ]);
    const queue = shuffle(birds.filter((bird) => reviewIds.has(bird.id)));
    if (!queue.length) {
      startDailyLearning();
      return;
    }
    setLearningMode("review");
    setLearningCards(queue);
    navigate("learn");
  };

  const startQuiz = () => {
    setQuizQuestions(shuffle(birds).slice(0, 10));
    navigate("quiz");
  };

  const openGroup = (group: BirdGroup) => {
    setLibraryGroup(group);
    navigate("library");
  };

  const openLibrary = () => {
    setLibraryGroup(undefined);
    navigate("library");
  };

  const openBird = (bird: Bird) => {
    setSelectedBird(bird);
    navigate("detail");
  };

  const studySingleBird = () => {
    if (!selectedBird) return;
    setLearningMode("daily");
    setLearningCards([selectedBird]);
    navigate("learn");
  };

  const rateBird = (
    id: string,
    mastery: "known" | "fuzzy" | "new"
  ) => {
    setMastery(id, mastery);
    markStudied();
  };

  const page = useMemo(() => {
    switch (view) {
      case "learn":
        return (
          <LearnView
            key={`${learningMode}-${learningCards.map((bird) => bird.id).join("-")}`}
            cards={learningCards}
            mode={learningMode}
            onBack={() => navigate("home")}
            onRate={rateBird}
            onContinue={() => navigate("home")}
          />
        );
      case "quiz":
        return (
          <QuizView
            key={quizQuestions.map((bird) => bird.id).join("-")}
            questions={quizQuestions}
            onBack={() => navigate("home")}
            onAnswer={recordQuizAnswer}
          />
        );
      case "library":
        return (
          <LibraryView
            key={libraryGroup ?? "all"}
            progress={progress}
            initialGroup={libraryGroup}
            onOpenBird={openBird}
          />
        );
      case "detail":
        return selectedBird ? (
          <BirdDetailView
            bird={selectedBird}
            mastery={progress.mastery[selectedBird.id]}
            onBack={() => navigate("library")}
            onStudy={studySingleBird}
          />
        ) : null;
      case "progress":
        return (
          <ProgressView
            progress={progress}
            stats={stats}
            onReview={startReview}
            onReset={resetProgress}
          />
        );
      default:
        return (
          <HomeView
            progress={progress}
            stats={stats}
            onStartLearning={startDailyLearning}
            onStartReview={startReview}
            onStartQuiz={startQuiz}
            onOpenGroup={openGroup}
          />
        );
    }
    // React memoization is only used to keep a focus session stable while local progress changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    learningCards,
    learningMode,
    quizQuestions,
    libraryGroup,
    selectedBird,
    progress,
    stats
  ]);

  const focusMode = view === "learn" || view === "quiz";
  const navView = view === "detail" ? "library" : view;

  const navItems = [
    { view: "home" as const, label: "首页", icon: Home },
    { view: "learn" as const, label: "学习", icon: BookOpen },
    { view: "quiz" as const, label: "测验", icon: ScanSearch },
    { view: "library" as const, label: "鸟库", icon: Library },
    { view: "progress" as const, label: "进度", icon: BarChart3 }
  ];

  const handleNav = (itemView: View) => {
    if (itemView === "learn") {
      startDailyLearning();
      return;
    }
    if (itemView === "quiz") {
      startQuiz();
      return;
    }
    if (itemView === "library") {
      openLibrary();
      return;
    }
    navigate(itemView);
  };

  return (
    <div className={`app ${focusMode ? "app--focus" : ""}`}>
      {!focusMode && (
        <header className="site-header">
          <button className="brand-button" onClick={() => navigate("home")}>
            <Brand compact />
          </button>
          <nav className="desktop-nav" aria-label="主导航">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = navView === item.view;
              return (
                <button
                  type="button"
                  key={item.view}
                  className={active ? "is-active" : ""}
                  onClick={() => handleNav(item.view)}
                  aria-current={active ? "page" : undefined}
                >
                  <Icon size={17} />
                  {item.label}
                </button>
              );
            })}
          </nav>
          <button className="header-cta" onClick={startDailyLearning}>
            今日学习
            <span>{stats.today}/{progress.dailyGoal}</span>
          </button>
        </header>
      )}

      {page}

      {!focusMode && (
        <>
          <footer className="site-footer">
            <Brand />
            <p>通过真实照片和对比观察，认识身边的中国鸟类。</p>
            <span>照片由课程素材提供 · 识别内容用于自然教育</span>
          </footer>
          <nav className="mobile-nav" aria-label="移动端主导航">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = navView === item.view;
              return (
                <button
                  type="button"
                  key={item.view}
                  className={active ? "is-active" : ""}
                  onClick={() => handleNav(item.view)}
                  aria-current={active ? "page" : undefined}
                >
                  <Icon size={21} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </>
      )}
    </div>
  );
};

export default App;
